import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CheckCircle, XCircle, Eye, Flag, Trash2, AlertTriangle, ExternalLink } from 'lucide-react';
import { StatusBadge } from './StatusBadge';

const SOCIAL_ICONS = {
  instagram: { label: 'IG', color: 'bg-pink-100 text-pink-600 hover:bg-pink-200' },
  tiktok: { label: 'TT', color: 'bg-stone-100 text-stone-700 hover:bg-stone-200' },
  twitter: { label: 'X', color: 'bg-sky-100 text-sky-600 hover:bg-sky-200' },
  facebook: { label: 'FB', color: 'bg-blue-100 text-blue-600 hover:bg-blue-200' },
};

function SocialChips({ socialLinks }) {
  if (!socialLinks) return <span className="text-xs text-stone-300">,</span>;
  const entries = Object.entries(socialLinks).filter(([, v]) => v && v.trim());
  if (entries.length === 0) return <span className="text-xs text-stone-300">,</span>;
  return (
    <div className="flex gap-1 flex-wrap">
      {entries.map(([platform, handle]) => {
        const cfg = SOCIAL_ICONS[platform] || { label: platform[0].toUpperCase(), color: 'bg-stone-100 text-stone-500' };
        const prefixes = { instagram: 'https://instagram.com/', tiktok: 'https://tiktok.com/@', twitter: 'https://x.com/', facebook: 'https://facebook.com/' };
        const url = handle.startsWith('http') ? handle : `${prefixes[platform] || ''}${handle.replace('@', '')}`;
        return (
          <a key={platform} href={url} target="_blank" rel="noopener noreferrer"
            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors ${cfg.color}`}
            title={`${platform}: @${handle.replace('@', '')}`}
            data-testid={`social-chip-${platform}`}
          >
            {cfg.label}
            <ExternalLink className="h-2.5 w-2.5" />
          </a>
        );
      })}
    </div>
  );
}

export function AdminProvidersTab({
  filteredProviders, providerStatusFilter, setProviderStatusFilter,
  onStatusChange, onFlag, onDelete, onViewProvider
}) {
  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <Select value={providerStatusFilter} onValueChange={setProviderStatusFilter}>
          <SelectTrigger className="w-40 input-zuriya" data-testid="provider-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="deleted">Deleted</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-stone-400">{filteredProviders.length} providers</span>
      </div>
      <div className="card-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F9F6F0]">
              <tr>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Provider</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Email</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Location</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Status</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Socials</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Rating</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProviders.map((p) => (
                <tr key={p.provider_id} className={`border-b border-[#E6E4DD] hover:bg-[#F9F6F0] ${p.flagged ? 'bg-red-50' : ''}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      {p.flagged && <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                      <div>
                        <p className="font-medium text-[#1A1A1A] text-sm">{p.brand_name}</p>
                        <p className="text-xs text-stone-400">@{p.username}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-stone-600">{p.user_email}</td>
                  <td className="py-3 px-4 text-sm text-stone-600">{p.location || 'N/A'}</td>
                  <td className="py-3 px-4"><StatusBadge status={p.status} /></td>
                  <td className="py-3 px-4"><SocialChips socialLinks={p.social_links} /></td>
                  <td className="py-3 px-4 text-sm">{p.rating > 0 ? `${p.rating.toFixed(1)} (${p.review_count})` : ','}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1 flex-wrap">
                      <Button size="sm" variant="ghost" onClick={() => onViewProvider(p)} title="View" className="h-8 px-2">
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      {p.status === 'pending' && (
                        <>
                          <Button size="sm" className="btn-primary h-8 px-2" onClick={() => onStatusChange(p.provider_id, 'approved')} data-testid={`approve-${p.provider_id}`}>
                            <CheckCircle className="h-3.5 w-3.5 mr-1" />Approve
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 h-8 px-2" onClick={() => onStatusChange(p.provider_id, 'rejected')}>
                            <XCircle className="h-3.5 w-3.5 mr-1" />Reject
                          </Button>
                        </>
                      )}
                      {p.status === 'approved' && (
                        <Button size="sm" variant="outline" className="text-yellow-600 h-8 px-2" onClick={() => onStatusChange(p.provider_id, 'pending')}>Suspend</Button>
                      )}
                      {p.status === 'rejected' && (
                        <Button size="sm" className="btn-primary h-8 px-2" onClick={() => onStatusChange(p.provider_id, 'approved')}>Approve</Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => onFlag(p.provider_id)} title={p.flagged ? 'Unflag' : 'Flag'} className={`h-8 px-2 ${p.flagged ? 'text-red-500' : 'text-stone-400'}`}>
                        <Flag className="h-3.5 w-3.5" />
                      </Button>
                      {p.status !== 'deleted' && (
                        <Button size="sm" variant="ghost" onClick={() => onDelete(p.provider_id)} title="Delete" className="h-8 px-2 text-red-400 hover:text-red-600">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
